import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../css/frontend/qrcode-page.css';

import {
    FaPhoneAlt,
    FaEnvelope,
    FaMapMarkerAlt,
    FaFacebookF,
    FaInstagram,
    FaTelegramPlane,
    FaGlobeEurope,
    FaInfoCircle,
} from 'react-icons/fa';

export default function QrCodeLandingPage() {
    const { t } = useTranslation();

    return (
        <>
            <Helmet>
                <title>SpaceTime Community | Links</title>
                <meta
                    name="description"
                    content="Quick access links to SpaceTime Community: contact, map, social media and website."
                />
                <meta property="og:title" content="SpaceTime Community | Quick Links" />
                <meta
                    property="og:description"
                    content="Open our social media, contact us, or find the Lab on the map with one tap."
                />
                <meta property="og:url" content="https://spacetime.community/qrcode" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            <div className="qr-landing">
                {/* Top header / logo area */}
                <header className="qr-header">
                    <div className="qr-logo">
                        <img
                            src="/img/spacetime_logo_small.png"
                            alt="SpaceTime Community logo"
                            className="qr-logo-img"
                        />
                    </div>
                    <h1 className="qr-title">
                        {t('QR_TITLE') || 'SpaceTime Community'}
                    </h1>
                    <p className="qr-subtitle">
                        {t('QR_SUBTITLE') || 'Choose what you need and tap to open.'}
                    </p>
                </header>

                {/* Main link groups */}
                <main className="qr-main">
                    {/* Site navigation shortcuts */}
                    <section className="qr-group">
                        <h2 className="qr-group-title">
                            {t('QR_SITE_LINKS') || 'More from the website'}
                        </h2>
                        <div className="qr-buttons-grid">
                            <Link to="/" className="qr-btn qr-btn-ghost">
                                <FaGlobeEurope className="qr-btn-icon" />
                                <span className="qr-btn-text">
                                    {t('GO_TO_WEBSITE') || 'Homepage'}
                                </span>
                            </Link>

                            <Link to="/information" className="qr-btn qr-btn-ghost">
                                <FaInfoCircle className="qr-btn-icon" />
                                <span className="qr-btn-text">
                                    {t('Information') || 'Information page'}
                                </span>
                            </Link>
                        </div>
                    </section>
                    {/* Socials */}
                    <section className="qr-group">
                        <h2 className="qr-group-title">
                            {t('QR_SOCIALS') || 'Social media'}
                        </h2>
                        <div className="qr-buttons-grid">
                            <a
                                href="https://facebook.com/spacetime.community/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="qr-btn qr-btn-social fb"
                            >
                                <FaFacebookF className="qr-btn-icon" />
                                <span className="qr-btn-text">Facebook</span>
                            </a>

                            <a
                                href="https://instagram.com/spacetime.community"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="qr-btn qr-btn-social ig"
                            >
                                <FaInstagram className="qr-btn-icon" />
                                <span className="qr-btn-text">Instagram</span>
                            </a>

                            <a
                                href="https://t.me/spacetime_community"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="qr-btn qr-btn-social tg"
                            >
                                <FaTelegramPlane className="qr-btn-icon" />
                                <span className="qr-btn-text">Telegram</span>
                            </a>
                        </div>
                    </section>
                    {/* Primary actions */}
                    <section className="qr-group">
                        <h2 className="qr-group-title">
                            {t('QR_PRIMARY_ACTIONS') || 'Quick actions'}
                        </h2>
                        <div className="qr-buttons-grid">
                            <a
                                href="tel:+306946618047"
                                className="qr-btn qr-btn-primary"
                            >
                                <FaPhoneAlt className="qr-btn-icon" />
                                <span className="qr-btn-text">
                                    {t('CALL_US') || 'Call us'}
                                </span>
                            </a>

                            <a
                                href="mailto:info@spacetime.community"
                                className="qr-btn qr-btn-primary"
                            >
                                <FaEnvelope className="qr-btn-icon" />
                                <span className="qr-btn-text">
                                    {t('SEND_EMAIL') || 'Send email'}
                                </span>
                            </a>

                            <a
                                href="https://maps.app.goo.gl/C3d1SfnbsC9LwRyPA"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="qr-btn qr-btn-primary"
                            >
                                <FaMapMarkerAlt className="qr-btn-icon" />
                                <span className="qr-btn-text">
                                    {t('OPEN_MAPS') || 'Open in Maps'}
                                </span>
                            </a>
                        </div>
                    </section>
                </main>

                {/* Footer / small print */}
                <footer className="qr-footer">
                    <p className="qr-footer-text">
                        Mirarchou Dimitriou Koufitsa 18, Thessaloniki 546 35
                    </p>
                </footer>
            </div>
        </>
    );
}
